import { useMemo, useCallback, useEffect } from 'react';
import { DateTime } from 'luxon';
import {
  SortOrder,
  TimeSignalClassification,
  TimeSignalFilterInput,
  TimeSignalsInput,
  TimeSignalSortField,
  TimeSignalsQuery,
  TimeSignalLabelValueFilterInput
} from 'graphql/types';
import { SearchCriteria } from 'modules/notes/common/components/SearchFacets';
import { LabelValueType } from 'modules/notes/common/components/SearchFacets/LabelFacetDetails';
import { dateRangeTag } from 'modules/notes/common/components/SearchFacets/DateRangeFacet';
import { MIN_SUPPORTED_DATE } from 'utils/date-time';
import { customLabelsTag } from 'modules/notes/common/components/SearchFacets/CustomLabelFacet/tag';
import { useMeContext } from 'modules/me/MeContextProvider';
import {
  NotesClassification,
  useNotesFilterContext
} from '../NotesFilterContextProvider';
import { useNotesSortContext } from '../NotesSortContextProvider';
import {
  cacheDates,
  clearCachedDates,
  getCachedDates
} from '../cache/utils/time-signal-utils';

export type TimeSignalGetParam = {
  input: TimeSignalsInput;
};
export const ALL_CLASSIFICATIONS = [
  TimeSignalClassification.Unclassified,
  TimeSignalClassification.Private,
  TimeSignalClassification.Accepted
];

export const getLabelValues = (
  searchCriteria: SearchCriteria
): TimeSignalLabelValueFilterInput[] | undefined => {
  const { criterions } = searchCriteria;

  const labelValues = Object.keys(criterions)
    .filter((key) => key !== dateRangeTag)
    .reduce((acc, key) => {
      const _values = criterions[key] as LabelValueType[];

      let values: { labelId: string; idValues: string[] }[] = [];
      if (key === customLabelsTag) {
        values = _values.map((v) => ({
          labelId: v.labelId,
          idValues: (v.values ?? []).map((val) => val.id)
        }));
      } else {
        values =
          _values && _values.length > 0
            ? [
                {
                  labelId: _values[0].labelId,
                  idValues: _values.map((value) => value.value)
                }
              ]
            : [];
      }
      return [...acc, ...values];
    }, [] as TimeSignalLabelValueFilterInput[]);

  return labelValues.length > 0 ? labelValues : undefined;
};

const getTimeSignalVariable = ({
  fromDate,
  toDate,
  selectedClassification,
  searchCriteria,
  defaultPageCount,
  sortOrder
}: {
  fromDate?: DateTime | null;
  toDate?: DateTime | null;
  selectedClassification: NotesClassification;
  searchCriteria: SearchCriteria;
  defaultPageCount?: number;
  sortOrder: SortOrder;
}): TimeSignalGetParam => {
  const { keywords } = searchCriteria;

  return {
    input: {
      filter: {
        fromDate:
          fromDate && fromDate < MIN_SUPPORTED_DATE
            ? MIN_SUPPORTED_DATE.toISODate()
            : fromDate?.toISODate() || undefined,
        toDate: toDate?.toISODate() || undefined,
        classifications:
          selectedClassification === 'All'
            ? ALL_CLASSIFICATIONS
            : [TimeSignalClassification[selectedClassification]],
        searchText:
          keywords && keywords.length > 0
            ? keywords.join(' ').toLowerCase()
            : undefined,
        labelValues: getLabelValues(searchCriteria)
      },
      count: defaultPageCount ?? 30,
      sortFields: [
        {
          sortField: TimeSignalSortField.Date,
          sortOrder: SortOrder.Descending
        },
        { sortField: TimeSignalSortField.StartTime, sortOrder: sortOrder },
        { sortField: TimeSignalSortField.EndTime, sortOrder: sortOrder }
      ]
    }
  };
};

export const useTimeSignalGetParam = (
  pageCount?: number
): TimeSignalGetParam => {
  const {
    filterContext: { selectedDateRange, selectedClassification, searchCriteria }
  } = useNotesFilterContext();
  const { sortOrder } = useNotesSortContext();
  return useMemo(() => {
    return getTimeSignalVariable({
      fromDate: selectedDateRange.start,
      toDate: selectedDateRange.end,
      selectedClassification,
      searchCriteria,
      defaultPageCount: pageCount,
      sortOrder
    });
  }, [
    selectedDateRange.start,
    selectedDateRange.end,
    selectedClassification,
    searchCriteria,
    pageCount,
    sortOrder
  ]);
};

export type UpdateFilterParams = {
  includeIds?: string[];
  excludeIds?: string[];
  fromDate?: DateTime;
  toDate?: DateTime;
};

export const useTimeSignalUpdateFilter = (): ((
  params: UpdateFilterParams
) => TimeSignalFilterInput) => {
  const {
    filterContext: { selectedDateRange, selectedClassification, searchCriteria }
  } = useNotesFilterContext();

  return useCallback(
    ({ fromDate, toDate, includeIds, excludeIds }: UpdateFilterParams = {}) => {
      const { keywords } = searchCriteria ?? {};

      return {
        fromDate:
          (fromDate ?? selectedDateRange.start)?.toISODate() ?? undefined,
        toDate: (toDate ?? selectedDateRange.end)?.toISODate() ?? undefined,
        classifications:
          selectedClassification === 'All'
            ? ALL_CLASSIFICATIONS
            : [TimeSignalClassification[selectedClassification]],
        includeIds: includeIds ?? null,
        excludeIds: excludeIds ?? null,
        searchText:
          keywords && keywords.length > 0
            ? keywords.join(' ').toLowerCase()
            : null,
        labelValues: getLabelValues(searchCriteria)
      };
    },
    [
      searchCriteria,
      selectedClassification,
      selectedDateRange.end,
      selectedDateRange.start
    ]
  );
};

export const useGetUniqueDateRange = (
  timeSignalsQueryData: TimeSignalsQuery | undefined
): DateTime[] => {
  const {
    filterContext: {
      selectedClassification,
      searchCriteria,
      selectedDateRange,
      selectedPeriod,
      pageType
    }
  } = useNotesFilterContext();
  const { id } = useMeContext() ?? {};
  useEffect(() => {
    clearCachedDates(id);
  }, [
    id,
    selectedClassification,
    searchCriteria,
    selectedDateRange,
    selectedPeriod,
    pageType
  ]);

  const dateSet = useMemo(() => {
    return Array.from(
      new Set(
        (timeSignalsQueryData?.timeSignals?.items ?? []).map(
          ({ date }) => date as string
        )
      )
    );
  }, [timeSignalsQueryData?.timeSignals?.items]);

  const _uniqueDates = useMemo(() => {
    const timeSignalsTotalMap = getCachedDates(id);

    const results =
      timeSignalsTotalMap.length == 0
        ? dateSet
        : dateSet.filter((d) => !timeSignalsTotalMap.includes(d));
    cacheDates(id, dateSet);
    return results;
  }, [dateSet, id]);

  const uniqueDates = useMemo(() => {
    return (_uniqueDates.length > 0
      ? _uniqueDates
      : [DateTime.local().toISODate()]
    ).map((date) => DateTime.fromISO(date));
  }, [_uniqueDates]);
  return uniqueDates;
};
